import React, { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  createFeedbackSubmissionId,
  submitChatFeedback,
} from '../../api/stageFunctions';
import type { FeedbackSourceTuple } from '../../feedback/feedbackSourceContext';
import type { Turn } from './types';

interface Props {
  /** The completed Q&A turn this feedback is about. */
  turn: Turn;
  /** Friendly surface label for the email + rate-limit bucket ("Ask FAR" | "Homepage chat"). */
  surface: string;
  /** Workflow/flow id (FarChat) or chat session id (HomeChat); may be null. */
  contextId: string | null;
  /** Semantic parent page for aggregation; distinct from the chat rate-limit surface. */
  source?: FeedbackSourceTuple;
}

/**
 * Per-answer "Report an issue" control, shown under each Ask FAR answer.
 *
 * Collapsed it is a subtle flag link; clicking expands an inline textarea +
 * Send that emails the maintainers (via the shared `/api/feedback` endpoint)
 * with this specific question + answer attached. On success the control is
 * replaced by a short thank-you so the user gets clear confirmation.
 */
const AnswerFeedback: React.FC<Props> = ({ turn, surface, contextId, source }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submissionId = useRef<string>();
  if (!submissionId.current) {
    submissionId.current = createFeedbackSubmissionId();
  }

  if (sent) {
    return (
      <Box
        data-testid="farchat-answer-feedback-sent"
        sx={{
          alignSelf: 'flex-start',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color: 'success.main',
          fontSize: '0.78rem',
          pl: 0.5,
        }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: '1rem' }} />
        Thanks — your feedback was sent to the team.
      </Box>
    );
  }

  if (!open) {
    return (
      <Button
        size="small"
        variant="outlined"
        startIcon={<OutlinedFlagIcon sx={{ fontSize: '1rem' }} />}
        onClick={() => setOpen(true)}
        data-testid="farchat-answer-feedback-open"
        sx={{
          alignSelf: 'flex-start',
          textTransform: 'none',
          color: 'text.secondary',
          borderColor: 'divider',
          borderRadius: 5,
          fontSize: '0.78rem',
          fontWeight: 600,
          minWidth: 0,
          px: 1.25,
          py: 0.25,
          mt: 0.25,
          '&:hover': {
            color: 'warning.dark',
            borderColor: 'warning.main',
            bgcolor: 'action.hover',
          },
        }}
      >
        Report an issue with this answer
      </Button>
    );
  }

  const handleSend = async () => {
    const msg = text.trim();
    if (!msg) return;
    setSending(true);
    setError(null);
    const answer = [turn.summary, turn.detail].filter(Boolean).join('\n\n');
    const res = await submitChatFeedback({
      message: msg,
      surface,
      question: turn.user_msg,
      answer,
      turnId: turn.turn_id,
      contextId,
      submissionId: submissionId.current,
      source,
    });
    setSending(false);
    if (res.success) {
      setSent(true);
    } else {
      setError(res.error || 'Failed to send feedback. Please try again.');
    }
  };

  return (
    <Box
      data-testid="farchat-answer-feedback-form"
      sx={{
        alignSelf: 'flex-start',
        width: '85%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        p: 1,
        borderRadius: 2,
        border: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}>
        What was wrong with this answer?
      </Box>
      <TextField
        autoFocus
        multiline
        minRows={3}
        maxRows={8}
        fullWidth
        size="small"
        placeholder="Tell us what was inaccurate, missing, or unclear…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={sending}
        inputProps={{ maxLength: 5000 }}
      />
      {error && (
        <Box sx={{ fontSize: '0.75rem', color: 'error.main' }}>{error}</Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          size="small"
          onClick={() => {
            setOpen(false);
            setText('');
            setError(null);
            submissionId.current = createFeedbackSubmissionId();
          }}
          disabled={sending}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSend}
          disabled={!text.trim() || sending}
          sx={{ textTransform: 'none' }}
        >
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </Box>
    </Box>
  );
};

export default AnswerFeedback;
